import { supabase } from '../lib/supabaseClient';
import { PrimeIndemnite, StatusType } from '../types';
import { logger } from '../utils/logger';
import { Heart, Gift, Clock, Stethoscope, Users, Shield, Moon, Bus, Baby, AlertTriangle, Scale, Activity, Calendar, Globe, Home, Footprints, Award, Skull, UserCheck, HandHeart, GraduationCap } from 'lucide-react';

/**
 * @fileoverview Service for fetching and transforming salaire_hopital data from Supabase
 * 
 * This service handles all interactions with the salaire_hopital table in Supabase,
 * including data fetching, error handling, and transformation to the application's
 * expected PrimeIndemnite structure.
 * 
 * @author App'Ines Team
 * @version 1.0.0
 * @since 2025
 */

/**
 * Raw data structure from Supabase salaire_hopital table
 */
export interface SalaireHopitalRow {
  id: number;
  created_at: string;
  code_name: string;
  nom: string;
  description: string;
  infos: string;
  montant_defaut: number;
  calculable: boolean;
  type: string;
  details: string;
  proratisee: boolean;
  formule_code: string;
  url_doc: string;
  order: number;
  echelons_eligibilite: string;
  const_taux_pss: number;
  const_iss_numerateur: number;
  const_iss_denominateur: number;
  const_transport_base_defaut: number;
  const_transport_taux_remboursement: number;
  const_ir_taux_zone1: number;
  const_ir_taux_zone2: number;
  const_ir_taux_zone3: number;
  const_iftd_montant_forfaitaire: number;
  const_contagion_montant_demi_journee: number;
  const_contagion_jours_defaut: number;
  const_prime_nuit_majoration: number;
  const_sft_im_plancher: number;
  const_sft_im_plafond: number;
  const_sft_part_fixe_1enfant: number;
  const_sft_part_fixe_2enfants: number;
  const_sft_part_fixe_3enfants: number;
  const_sft_part_fixe_enfant_supp: number;
  const_sft_part_prop_1enfant: string;
  const_sft_part_prop_2enfants: number;
  const_sft_part_prop_3enfants: string;
  const_sft_part_prop_enfant_supp: string;
  const_service_max_amount: number;
  config_iftd_dimanches_par_mois_defaut: number;
  config_iftd_jours_feries_defaut: number;
  config_contagion_jours_travailles_defaut: number;
  config_prime_nuit_nuits_par_mois_defaut: number;
  config_sft_nombre_enfants_defaut: number;
  professions_eligibles: string;
  insalubrite_cat_2: number;
  cti_points: number;
  service_taux_max: number;
  prime_type: string;
  eligibilite: string;
  a_negocier: boolean;
  base_legale?: string;
  decret_reference?: string;
  date_revalorisation?: string;
  note_calcul?: string;
}

/**
 * Maps code_name to appropriate Lucide React icons
 */
function getIconForPrime(codeName: string): any {
  const iconMap: Record<string, any> = {
    'segur': Heart,
    'transport': Bus,
    'indemnite_residence': Home,
    'iss': Clock,
    'pss': AlertTriangle,
    'debut_carriere': Baby,
    'veil': Scale,
    'soins_critiques': Activity,
    'iftd': Calendar,
    'isg_dom': Globe,
    'contagion': Shield,
    'prime_nuit': Moon,
    'sft': Users,
    'service': Stethoscope,
    'chaussures': Footprints,
    'insalubrite': Skull,
    'nbi': Award,
    'grand_age': UserCheck,
    'engagement_collectif': HandHeart,
    'tutorat': GraduationCap,
    'default': Gift
  };
  
  return iconMap[codeName] || iconMap['default'];
}

/**
 * Maps code_name to appropriate color themes
 */
function getColorForPrime(codeName: string): string {
  const colorMap: Record<string, string> = {
    'segur': 'green',
    'transport': 'blue',
    'indemnite_residence': 'cyan',
    'iss': 'green',
    'pss': 'slate',
    'debut_carriere': 'orange',
    'veil': 'pink',
    'soins_critiques': 'red',
    'iftd': 'teal',
    'isg_dom': 'orange',
    'contagion': 'red',
    'prime_nuit': 'slate',
    'sft': 'emerald',
    'service': 'blue',
    'chaussures': 'amber',
    'insalubrite': 'amber',
    'nbi': 'teal',
    'grand_age': 'amber',
    'engagement_collectif': 'emerald',
    'tutorat': 'blue',
  };
  
  return colorMap[codeName] || 'gray';
}

/**
 * Maps database type to PrimeIndemnite type
 */
function mapPrimeType(dbType: string): 'fixe' | 'variable' | 'pourcentage' {
  switch (dbType?.toLowerCase()) {
    case 'fixe':
      return 'fixe';
    case 'variable':
      return 'variable';
    case 'pourcentage':
      return 'pourcentage';
    default:
      return 'fixe';
  }
}

/**
 * Parses professions_eligibles string to array
 */
function parseProfessionsEligibles(professionsString: string): string[] {
  if (!professionsString) return ['infirmier'];

  try {
    if (professionsString.startsWith('[')) {
      return JSON.parse(professionsString);
    }
    return professionsString.split(',').map(p => p.trim()).filter(p => p.length > 0);
  } catch (error) {
    logger.warn('parseProfessionsEligibles - Error parsing:', error);
    return ['infirmier'];
  }
}

/**
 * Parses echelons_eligibilite string to array of numbers
 */
function parseEchelonsEligibles(echelonsString: string): number[] | null {
  if (!echelonsString || echelonsString.trim() === '') return null; // Null means all echelons are eligible
  
  try {
    // Handle JSON array format
    if (echelonsString.startsWith('[')) {
      return JSON.parse(echelonsString);
    }
    
    // Handle comma-separated format
    const echelons = echelonsString.split(',')
      .map(e => parseInt(e.trim()))
      .filter(e => !isNaN(e)); // Accept all valid numbers including negative and zero
    
    return echelons.length > 0 ? echelons : null;
  } catch (error) {
    logger.warn('Error parsing echelons_eligibilite:', error);
    return null;
  }
}

/**
 * Parses eligibilite string to array of StatusType
 */
function parseEligibiliteStatuses(eligibiliteString: string): StatusType[] {
  const defaultStatuses: StatusType[] = ['titulaire', 'stagiaire', 'contractuel'];
  if (!eligibiliteString) return defaultStatuses;

  try {
    if (eligibiliteString.startsWith('[')) {
      return JSON.parse(eligibiliteString);
    }
    const filtered = eligibiliteString
      .split(',')
      .map(s => s.trim() as StatusType)
      .filter(s => defaultStatuses.includes(s));
    return filtered.length > 0 ? filtered : defaultStatuses;
  } catch (error) {
    logger.warn('parseEligibiliteStatuses - Error parsing:', error);
    return defaultStatuses;
  }
}

/**
 * Fetches all salaire_hopital data from Supabase
 * 
 * @returns {Promise<SalaireHopitalRow[]>} Array of raw prime data from Supabase
 * @throws {Error} If the fetch operation fails
 * @public This function is exported for use by the prime generation script
 */
export async function fetchSalaireHopitalPrimes(): Promise<SalaireHopitalRow[]> {
  try {
    const { data, error } = await supabase
      .from('salaire_hopital')
      .select('*')
      .order('order', { ascending: true });

    if (error) {
      logger.error('Supabase error fetching salaire_hopital:', error);
      throw new Error(`Failed to fetch primes data: ${error.message}`);
    }

    if (!data) {
      throw new Error('No data returned from salaire_hopital table');
    }

    return data;
  } catch (error) {
    logger.error('Error in fetchSalaireHopitalPrimes:', error);
    throw error;
  }
}

/**
 * Transforms raw Supabase data into PrimeIndemnite structure
 * 
 * This function maps the database columns to the application's PrimeIndemnite interface,
 * handling type conversions, default values, and special configurations.
 * 
 * @param {SalaireHopitalRow[]} rawData - Raw data from Supabase
 * @returns {PrimeIndemnite[]} Transformed data ready for use in the application
 * @public This function is exported for use by the prime generation script
 */
export function transformSalaireHopitalToPrimes(rawData: SalaireHopitalRow[]): PrimeIndemnite[] {
  return rawData
    .filter(row => row.code_name && row.nom)
    .map(row => {
      const prime: PrimeIndemnite = {
        id: row.code_name,
        nom: row.nom,
        description: row.description || '',
        montantDefaut: Number(row.montant_defaut) || 0,
        montantUtilisateur: Number(row.montant_defaut) || 0,
        active: false, // Always start inactive
        type: mapPrimeType(row.type),
        icon: getIconForPrime(row.code_name),
        color: getColorForPrime(row.code_name),
        details: row.infos || row.details || undefined,
        calculable: Boolean(row.calculable),
        availableForProfessions: parseProfessionsEligibles(row.professions_eligibles),
        eligibleStatuses: parseEligibiliteStatuses(row.eligibilite),
        proratisee: Boolean(row.proratisee),
        formuleCode: row.formule_code || undefined,
        calculDetails: row.infos || row.details || undefined, // Map infos to calculDetails for display
        urlDoc: row.url_doc || undefined,
        ordre: Number(row.order) || 0,
        eligibleEchelons: parseEchelonsEligibles(row.echelons_eligibilite),
        primeType: row.prime_type || undefined,
        aNegocier: Boolean(row.a_negocier),
        baseLegale: row.base_legale || undefined,
        decretReference: row.decret_reference || undefined,
        dateRevalorisation: row.date_revalorisation || undefined,
        noteCalcul: row.note_calcul || undefined
      };

      // Add CTI points if available
      if (row.cti_points && row.cti_points > 0) {
        prime.ctiPoints = Number(row.cti_points);
      }

      // Add service taux max if available
      if (row.service_taux_max && row.service_taux_max > 0) {
        prime.serviceTauxMax = Number(row.service_taux_max);
        prime.isMaxCalculated = true;
      }

      // Add transport-specific properties
      if (row.code_name === 'transport') {
        prime.montantBase = Number(row.const_transport_base_defaut) || 40;
        prime.pourcentageRemboursement = Number(row.const_transport_taux_remboursement) || 75;
        prime.montantUtilisateur = (prime.montantBase * prime.pourcentageRemboursement) / 100;
      }

      // Add IFTD configuration
      if (row.code_name === 'iftd') {
        prime.iftdConfig = {
          dimanchesParMois: Number(row.config_iftd_dimanches_par_mois_defaut) || 0,
          joursFeriesTravailles: []
        };
      }

      // Add contagion configuration
      if (row.code_name === 'contagion') {
        prime.contagionConfig = {
          joursTravaillesParAn: Number(row.config_contagion_jours_travailles_defaut) || 228
        };
        // Calculate default amount based on configuration
        const joursTravailles = prime.contagionConfig.joursTravaillesParAn;
        const montantDemiJournee = Number(row.const_contagion_montant_demi_journee) || 0.33;
        prime.montantUtilisateur = Math.round((montantDemiJournee * 2 * joursTravailles / 12) * 100) / 100;
      }

      // Add prime nuit configuration
      if (row.code_name === 'prime_nuit') {
        prime.primeNuitConfig = {
          nuitsParMois: Number(row.config_prime_nuit_nuits_par_mois_defaut) || 0
        };
      }

      // Add SFT configuration
      if (row.code_name === 'sft') {
        prime.sftConfig = {
          nombreEnfants: Number(row.config_sft_nombre_enfants_defaut) || 0
        };
      }

      // Add NBI configuration
      if (row.code_name === 'nbi') {
        prime.nbiConfig = {
          points: 15 // Valeur par défaut
        };
      }

      // Add insalubrité cat 2 configuration
      if (row.code_name === 'insalubrite') {
        prime.montantDemiJourneeInsalubrite = Number(row.insalubrite_cat_2) || 0.31;
        prime.insalubriteCat2Config = {
          joursTravaillesParMois: 22
        };
        const joursTravailles = 22;
        const montantDemiJournee = Number(row.insalubrite_cat_2) || 0.31;
        prime.montantUtilisateur = Math.round((montantDemiJournee * 2 * joursTravailles) * 100) / 100;
      }

      return prime;
    });
}

/**
 * Fetches and transforms salaire_hopital data in one operation
 * 
 * This is a convenience function that combines fetching and transformation
 * into a single operation for easier use in React components.
 * 
 * @returns {Promise<PrimeIndemnite[]>} Transformed prime data ready for use
 * @throws {Error} If fetch or transformation fails
 */
export async function getSalaireHopitalPrimes(): Promise<PrimeIndemnite[]> {
  try {
    const rawData = await fetchSalaireHopitalPrimes();
    return transformSalaireHopitalToPrimes(rawData);
  } catch (error) {
    logger.error('Error in getSalaireHopitalPrimes:', error);
    throw error;
  }
}

/**
 * Validates that the fetched prime data is complete and consistent
 * 
 * @param {PrimeIndemnite[]} primes - Transformed prime data to validate
 * @returns {boolean} True if data is valid, false otherwise
 */
export function validatePrimeData(primes: PrimeIndemnite[]): boolean {
  if (!primes || primes.length === 0) {
    logger.warn('No primes data available');
    return false;
  }

  const requiredPrimes = ['segur', 'transport', 'pss', 'iss'];
  const availablePrimeIds = primes.map(p => p.id);

  for (const requiredId of requiredPrimes) {
    if (!availablePrimeIds.includes(requiredId)) {
      logger.warn(`Required prime "${requiredId}" is missing from data`);
      return false;
    }
  }

  for (const prime of primes) {
    if (!prime.id || !prime.nom || typeof prime.montantDefaut !== 'number') {
      logger.warn(`Invalid prime structure for prime:`, prime);
      return false;
    }
  }

  return true;
}